import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Mail, Lock, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

export default function Auth() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState(params.get("role") || "learner");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (params.get("role")) setMode("register");
  }, [params]);

  useEffect(() => {
    if (user) navigate(user.role === "instructor" ? "/studio" : "/feed", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let u;
      if (mode === "login") u = await login(form.email, form.password);
      else u = await register(form.name, form.email, form.password, role);
      toast.success(`Welcome, ${u.name}!`);
      navigate(u.role === "instructor" ? "/studio" : "/feed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/feed";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const fill = (email) => setForm({ name: "", email, password: "Test123!" });

  return (
    <div className="min-h-screen bg-[#121212] flex items-center justify-center px-4 relative z-10">
      <div className="w-full max-w-md">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 mb-8 mx-auto" data-testid="auth-logo-btn">
          <Logo size={40} />
          <span className="font-display font-black text-2xl tracking-tight text-white">COMMUNITY<span className="text-[#FF6B6B]">PUMP</span></span>
        </button>

        <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-8">
          <h1 className="font-display font-bold text-3xl uppercase tracking-tight text-white mb-1">
            {mode === "login" ? "Welcome back" : "Join CommunityPump"}
          </h1>
          <p className="text-sm text-[#A0A0A0] mb-6">
            {mode === "login" ? "Sign in to continue your journey" : "Start training or coaching today"}
          </p>

          {mode === "register" && (
            <div className="grid grid-cols-2 gap-2 mb-5">
              {[
                { id: "learner", label: "I want to Train" },
                { id: "instructor", label: "I want to Coach" },
              ].map((r) => (
                <button
                  key={r.id}
                  type="button"
                  data-testid={`role-select-${r.id}`}
                  onClick={() => setRole(r.id)}
                  className={`py-3 rounded-xl text-sm font-semibold border transition-colors ${
                    role === r.id
                      ? "border-[#FF6B6B] bg-[#FF6B6B]/10 text-[#FF6B6B]"
                      : "border-[#2A2A2A] text-[#A0A0A0] hover:text-white"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0A0A0]" />
                <input
                  data-testid="auth-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Full name"
                  className="w-full bg-[#121212] border border-[#2A2A2A] rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-[#A0A0A0] focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]"
                />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0A0A0]" />
              <input
                data-testid="auth-email-input"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                className="w-full bg-[#121212] border border-[#2A2A2A] rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-[#A0A0A0] focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0A0A0]" />
              <input
                data-testid="auth-password-input"
                required
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Password"
                className="w-full bg-[#121212] border border-[#2A2A2A] rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-[#A0A0A0] focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]"
              />
            </div>
            <button
              data-testid="auth-submit-btn"
              disabled={busy}
              className="w-full py-3 rounded-xl bg-[#FF6B6B] text-black font-bold uppercase tracking-wide text-sm hover:brightness-110 transition-all disabled:opacity-50"
            >
              {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[#2A2A2A]" />
            <span className="text-xs text-[#A0A0A0] uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-[#2A2A2A]" />
          </div>

          <button
            data-testid="google-login-btn"
            onClick={googleLogin}
            className="w-full py-3 rounded-xl border border-[#2A2A2A] bg-[#121212] text-white font-semibold text-sm flex items-center justify-center gap-2 hover:border-[#A0A0A0] transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5 7.7.5 3.99 2.97 2.18 6.56l3.66 2.84C6.71 6.79 9.14 4.75 12 4.75z"/></svg>
            Continue with Google
          </button>

          <p className="text-center text-sm text-[#A0A0A0] mt-6">
            {mode === "login" ? "New to CommunityPump?" : "Already have an account?"}{" "}
            <button
              data-testid="auth-toggle-mode-btn"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-[#FF6B6B] font-semibold hover:underline"
            >
              {mode === "login" ? "Create account" : "Sign in"}
            </button>
          </p>

          {mode === "login" && (
            <div className="mt-4 pt-4 border-t border-[#2A2A2A] flex flex-wrap gap-2 justify-center">
              <button data-testid="demo-coach-btn" onClick={() => fill("coach@communitypump.com")} className="text-xs px-3 py-1.5 rounded-full bg-[#2A2A2A] text-[#A0A0A0] hover:text-white transition-colors">Demo Coach</button>
              <button data-testid="demo-learner-btn" onClick={() => fill("learner@communitypump.com")} className="text-xs px-3 py-1.5 rounded-full bg-[#2A2A2A] text-[#A0A0A0] hover:text-white transition-colors">Demo Learner</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
